import { useState } from 'react';
import * as Yup from 'yup';
import { Path, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { STANDARD_BUSINESS_HOURS } from './constants';
import { General } from './steps/General';
import { FleetAndHours } from './steps/FleetAndHours';
import { DepotFormValues } from './types';
import { FormPage } from '../../tailwindComponents/FormPage/FormPage';

const dayWorkingHoursSchema = Yup.object({
  working: Yup.boolean(),
  openingTime: Yup.string().when('working', {
    is: true,
    then: (schema) =>
      schema
        .required('Opening time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional(),
  }),
  closingTime: Yup.string().when('working', {
    is: true,
    then: (schema) =>
      schema
        .required('Closing time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional(),
  }),
});

const dayLoadingHoursSchema = Yup.object({
  operating: Yup.boolean(),
  openingTime: Yup.string().when('operating', {
    is: true,
    then: (schema) =>
      schema
        .required('Opening time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  closingTime: Yup.string().when('operating', {
    is: true,
    then: (schema) =>
      schema
        .required('Closing time is required')
        .matches(/^\d{2}:\d{2}$/, 'Must be in HH:mm format'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
});

const optionalNumberSchema = Yup.string()
  .optional()
  .nullable()
  .test(
    'min-if-present',
    'Must be at least 1',
    (value) => !value || Number(value) >= 1
  )
  .matches(/^\d*\.?\d*$/, 'Must be a valid number');

const schema = [
  Yup.object().shape({
    name: Yup.string().required('Name is required'),
    address: Yup.string()
      .required('Depot address is required')
      .min(3, 'Depot address has to be at least 3 characters long'),
    city: Yup.string().nullable(),
    country: Yup.string().nullable(),
    location: Yup.object().shape({
      lon: Yup.number(),
      lat: Yup.number(),
    }),
    description: Yup.string().optional().nullable(),
    contactName: Yup.string().optional().nullable(),
    contactEmail: Yup.string().optional().nullable().email('Must be a valid email'),
    phoneNumber: Yup.string()
      .optional()
      .nullable()
      .matches(/^\+?[0-9\s-]*$/, 'Must be a valid phone number'),
    loadingDocks: optionalNumberSchema,
  }),
  Yup.object().shape({
    haveOwnFleet: Yup.boolean(),
    articsOver26t: optionalNumberSchema,
    rigidsUnder18t: optionalNumberSchema,
    ldvsUnder3_5t: optionalNumberSchema,
    vansUnder1_5t: optionalNumberSchema,
    rigidsUnder26t: optionalNumberSchema,
    rigidsUnder7_5t: optionalNumberSchema,
    specialOther: optionalNumberSchema,
    schedule: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Schedule is required'),
    flexibleHoursOnDemand: Yup.boolean(),
    mondayWorkingHours: dayWorkingHoursSchema,
    tuesdayWorkingHours: dayWorkingHoursSchema,
    wednesdayWorkingHours: dayWorkingHoursSchema,
    thursdayWorkingHours: dayWorkingHoursSchema,
    fridayWorkingHours: dayWorkingHoursSchema,
    saturdayWorkingHours: dayWorkingHoursSchema,
    sundayWorkingHours: dayWorkingHoursSchema,
    mondayLoadingHours: dayLoadingHoursSchema,
    tuesdayLoadingHours: dayLoadingHoursSchema,
    wednesdayLoadingHours: dayLoadingHoursSchema,
    thursdayLoadingHours: dayLoadingHoursSchema,
    fridayLoadingHours: dayLoadingHoursSchema,
    saturdayLoadingHours: dayLoadingHoursSchema,
    sundayLoadingHours: dayLoadingHoursSchema,
  }),
];

export function DepotForm({
  onSubmit,
  onSaveDraft,
  isSubmitting,
  isSavingDraft,
  header,
  pages,
  initialValues,
}: {
  onSubmit: (data: DepotFormValues) => void;
  onSaveDraft?: (data: DepotFormValues) => void;
  isSubmitting: boolean;
  isSavingDraft?: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
  initialValues?: DepotFormValues;
}) {
  const [currentStep, setCurrentStep] = useState(1);

  const steps = ['General', 'Fleet & Hours'];

  const defaultFormValues: DepotFormValues = {
    name: '',
    address: '',
    postcode: '',
    city: '',
    country: '',
    location: {
      lon: 0,
      lat: 0,
    },
    images: [],
    depotImages: [],
    deletedImagesIds: [],
    description: '',
    contactName: '',
    contactEmail: '',
    phoneCountryCode: 'GB',
    phoneNumber: '',
    loadingDocks: '',
    haveOwnFleet: false,
    articsOver26t: '',
    rigidsUnder26t: '',
    rigidsUnder18t: '',
    rigidsUnder7_5t: '',
    ldvsUnder3_5t: '',
    vansUnder1_5t: '',
    specialOther: '',
    schedule: STANDARD_BUSINESS_HOURS[0],
    flexibleHoursOnDemand: false,
    mondayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    tuesdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    wednesdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    thursdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    fridayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    saturdayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    sundayWorkingHours: {
      working: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    mondayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    tuesdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    wednesdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    thursdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    fridayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    saturdayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
    sundayLoadingHours: {
      operating: false,
      openingTime: '06:00',
      closingTime: '18:00',
    },
  };

  const methods = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema[currentStep - 1]),
    mode: 'onChange',
    defaultValues: initialValues ?? defaultFormValues,
  });

  const { formState, control, setValue } = methods;

  const formSteps = [
    <General
      key={0}
      control={control}
      formState={formState}
      setValue={setValue}
    />,
    <FleetAndHours key={1} control={control} formState={formState} />,
  ];

  const stepFields: Record<number, Path<DepotFormValues>[]> = {
    1: ['name', 'address'],
  };

  return (
    <FormPage<DepotFormValues>
      header={header}
      steps={steps}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      onSaveDraft={onSaveDraft}
      isSubmitting={isSubmitting}
      isSavingDraft={isSavingDraft}
      stepFields={stepFields}
    >
      {formSteps[currentStep - 1]}
    </FormPage>
  );
}
